import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Calendar, MapPin, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useRegionalExperience } from "../hooks/useRegionalExperience";
import AppointmentRequestModal from "../components/schedule/AppointmentRequestModal";

// Main Schedule Component
export default function SchedulePage() {
  const { t } = useTranslation();
  const { siteConfig } = useRegionalExperience();
  const visit = siteConfig?.visitAnnouncement || { enabled: false };
  const [activeMonth, setActiveMonth] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoaded, setIsLoaded] = useState(false);
  const [scheduleItems, setScheduleItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [appointmentModalOpen, setAppointmentModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Memoized months calculation
  const months = useMemo(() => {
    const uniqueMonths = [...new Set(scheduleItems.map((item) => item.month))];
    // Sort months chronologically
    return uniqueMonths.sort((a, b) => {
      const monthOrder = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      return monthOrder.indexOf(a) - monthOrder.indexOf(b);
    });
  }, [scheduleItems]);

  // Memoized filtered items
  const filteredItems = useMemo(() => {
    return scheduleItems.filter(
      (item) =>
        (activeMonth === "All" || item.month === activeMonth) &&
        (searchQuery === "" ||
          item.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.note?.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  }, [scheduleItems, activeMonth, searchQuery]);

  // Format time slot helper with improved error handling — defined first so formatScheduleData can use it
  const formatTimeSlot = useCallback(
    (slot) => {
      let formattedStartDate = "Not Available";
      let formattedEndDate = "Not Available";
      let month = "Upcoming";
      let timeDisplay = t("schedule.timeTbd");

      try {
        if (slot.startDate) {
          const startDate = new Date(slot.startDate);

          if (isNaN(startDate.getTime())) {
            console.warn("Invalid start date:", slot.startDate);
          } else {
            // Itinerary dates are date-only values stored at midnight UTC, so
            // they must be READ in UTC. Formatted in the viewer's own zone, a
            // devotee in New Jersey or Atlanta (UTC-4) saw the previous day —
            // "August 26" for a stop that begins on the 27th.
            formattedStartDate = startDate.toLocaleDateString("en-US", {
              day: "numeric",
              month: "long",
              year: "numeric",
              timeZone: "UTC",
            });
            month = startDate.toLocaleDateString("en-US", {
              month: "long",
              timeZone: "UTC",
            });

            const startTime = startDate.toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "numeric",
              hour12: true,
              timeZone: "UTC",
            });

            if (slot.endDate) {
              const endDate = new Date(slot.endDate);

              if (!isNaN(endDate.getTime())) {
                formattedEndDate = endDate.toLocaleDateString("en-US", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                  timeZone: "UTC",
                });

                const endTime = endDate.toLocaleTimeString("en-US", {
                  hour: "numeric",
                  minute: "numeric",
                  hour12: true,
                  timeZone: "UTC",
                });

                timeDisplay = startTime !== endTime ? `${startTime} - ${endTime}` : startTime;
              }
            } else {
              formattedEndDate = formattedStartDate;
              timeDisplay = startTime;
            }

            if (slot.period) {
              const periodText = slot.period.charAt(0).toUpperCase() + slot.period.slice(1);
              timeDisplay = t("schedule.timeDisplayWithPeriod", {
                period: periodText,
                time: timeDisplay,
              });
            }
          }
        } else if (slot.period) {
          const periodText = slot.period.charAt(0).toUpperCase() + slot.period.slice(1);
          timeDisplay = t("schedule.timeDisplayPeriodTba", { period: periodText });
        }
      } catch (error) {
        console.error("Error formatting time slot:", slot, error);
      }

      return { formattedStartDate, formattedEndDate, month, timeDisplay };
    },
    [t]
  );

  // Format schedule data with enhanced error handling
  const formatScheduleData = useCallback(
    (data) => {
      if (!Array.isArray(data)) {
        console.warn("Invalid data format received:", data);
        return [];
      }

      return data.flatMap((item) => {
        try {
          // Legacy schedules carried a `place` field; the new CMS Schedule model has none.
          // Exclude only items explicitly marked non-USA; place-less CMS schedules pass through.
          // (This page is already gated by the USA-visit toggle, and the CMS holds only visit schedules.)
          if (item.place && item.place !== "USA") return [];

          // Handle empty or undefined timeSlots
          if (!item.timeSlots || item.timeSlots.length === 0) {
            const _maxPeople0 = typeof item.maxPeople === "number" ? item.maxPeople : 100;
            const _bookedSeats0 = typeof item.bookedSeats === "number" ? item.bookedSeats : 0;
            return [
              {
                id: item._id || `default-${Date.now()}-${Math.random().toString(16).slice(2)}`,
                startDate: "Not Available",
                endDate: "Not Available",
                time: "Time TBD",
                location: item.locations || t("schedule.locationTbd"),
                city: "",
                note: item.locations
                  ? t("schedule.noteDetailsForLocation", { location: item.locations })
                  : t("schedule.noteDetailsForEvent"),
                details: t("schedule.detailsComingSoon"),
                month: "Upcoming",
                request: Boolean(item.appointment),
                isHighlighted: false,
                type: item.type || "meeting",
                maxPeople: _maxPeople0,
                bookedSeats: _bookedSeats0,
                availableSeats: _maxPeople0 - _bookedSeats0,
              },
            ];
          }

          // Map each time slot to an event
          return item.timeSlots.map((slot) => {
            const { formattedStartDate, formattedEndDate, month, timeDisplay } =
              formatTimeSlot(slot);
            const locationLabel = item.locations || t("schedule.locationTbd");

            const _maxPeople = typeof item.maxPeople === "number" ? item.maxPeople : 100;
            const _bookedSeats = typeof item.bookedSeats === "number" ? item.bookedSeats : 0;

            return {
              id: slot._id || `slot-${Date.now()}-${Math.random().toString(16).slice(2)}`,
              startDate: formattedStartDate,
              endDate: formattedEndDate,
              time: timeDisplay,
              rawStartDate: slot.startDate,
              rawEndDate: slot.endDate,
              location: item.locations || t("schedule.locationTbd"),
              city: "",
              note: slot.period
                ? t("schedule.notePeriodSession", {
                    period: slot.period.charAt(0).toUpperCase() + slot.period.slice(1),
                    location: locationLabel,
                  })
                : t("schedule.noteSession", { location: locationLabel }),
              details: t("schedule.detailsJoinUs", { location: locationLabel }),
              month,
              isHighlighted: false,
              request: Boolean(item.appointment),
              type: item.type || "meeting",
              maxPeople: _maxPeople,
              bookedSeats: _bookedSeats,
              availableSeats: _maxPeople - _bookedSeats,
            };
          });
        } catch (error) {
          console.error("Error formatting schedule item:", item, error);
          return [];
        }
      });
    },
    [t, formatTimeSlot]
  );

  // Fetch schedule data with retry logic
  const fetchSchedule = useCallback(
    async (retryCount = 0) => {
      const maxRetries = 3;

      try {
        setLoading(true);
        setError(null);

        const response = await axios.get(import.meta.env.VITE_APP_SCHEDULE_API, {
          timeout: 30000, // 30 second timeout
        });

        if (response.data && response.data.data) {
          const formattedSchedule = formatScheduleData(response.data.data);
          setScheduleItems(formattedSchedule);
        } else {
          throw new Error("Invalid response format");
        }

        setLoading(false);
        setTimeout(() => setIsLoaded(true), 100);
      } catch (err) {
        console.error("Failed to fetch schedule:", err);

        if (retryCount < maxRetries) {
          console.log(`Retrying... Attempt ${retryCount + 1} of ${maxRetries}`);
          setTimeout(() => fetchSchedule(retryCount + 1), 2000 * (retryCount + 1));
          return;
        }

        let errorMessage = t("schedule.fetch.errorGeneric");

        if (err.code === "ECONNABORTED") {
          errorMessage = t("schedule.fetch.errorTimeout");
        } else if (err.response?.status >= 500) {
          errorMessage = t("schedule.fetch.errorServer");
        }

        setError(errorMessage);
        setLoading(false);
      }
    },
    [formatScheduleData, t]
  );

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  // Event handlers
  const handleRequestAppointment = useCallback((item) => {
    setSelectedItem(item);
    setAppointmentModalOpen(true);
  }, []);

  const handleAppointmentSubmit = useCallback((appointmentData) => {
    console.log("Appointment submitted:", appointmentData);
    // You can add additional handling here, such as updating UI state
  }, []);

  const handleCloseModal = useCallback(() => {
    setAppointmentModalOpen(false);
    setSelectedItem(null);
  }, []);

  const handleRetry = useCallback(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  // Loading State Component
  function LoadingState() {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <div className="w-16 h-16 mb-4">
          <div className="w-full h-full border-4 border-gray-200 border-t-[#e63946] rounded-full animate-spin"></div>
        </div>
        <p className="text-gray-600 font-medium">{t("schedule.loadingTitle")}</p>
        <p className="text-gray-500 text-sm mt-2">{t("schedule.loadingMessage")}</p>
      </div>
    );
  }

  if (!visit.enabled) {
    return (
      <main className="flex min-h-[70vh] items-center justify-center bg-[#f7faf9] px-4 py-24 text-center text-[#14201f]">
        <div className="max-w-xl">
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#9d1f1c]">
            {t("schedule.offState.appointments")}
          </p>
          <h1 className="mt-3 text-3xl font-semibold leading-tight md:text-4xl">
            {visit.offMessage || t("schedule.offState.noVisit")}
          </h1>
          <p className="mt-4 text-base leading-8 text-[#52615f]">
            {visit.offDescription || t("schedule.offState.description")}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a
              href="mailto:info@avdheshanandgmission.org"
              className="inline-flex items-center gap-2 rounded-md bg-[#9d1f1c] px-5 py-3 text-sm font-bold uppercase tracking-[0.12em] text-white transition hover:bg-[#7f1916]"
            >
              {t("schedule.offState.emailUs")}
            </a>
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-md border border-[#d8c7ba] px-5 py-3 text-sm font-bold uppercase tracking-[0.12em] text-[#241511] transition hover:border-[#9d1f1c] hover:text-[#9d1f1c]"
            >
              {t("schedule.offState.returnHome")}
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-gradient-to-br from-[#fcf5eb] to-white">
      <section className="relative overflow-hidden bg-[#241511] px-4 py-12 text-white sm:px-6 lg:px-8">
        {visit.backgroundImage ? (
          <>
            <img
              src={visit.backgroundImage}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full object-cover opacity-40"
            />
            <div className="absolute inset-0 bg-[#241511]/55" aria-hidden="true" />
          </>
        ) : null}
        <div className="relative mx-auto max-w-4xl text-center">
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#f5c45e]">
            {visit.eyebrow || t("schedule.usaVisit")}
          </p>
          <h1 className="mt-3 text-3xl font-semibold leading-tight md:text-4xl">
            {visit.headline || t("schedule.defaultHeadline")}
          </h1>
          {visit.subtext ? (
            <p className="mx-auto mt-4 max-w-2xl text-base leading-8 text-white/80">
              {visit.subtext}
            </p>
          ) : null}
          {(visit.dateRange || visit.citiesLine) && (
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {visit.dateRange ? (
                <span className="rounded-full bg-white/12 px-3 py-1.5 text-xs font-semibold backdrop-blur">
                  {visit.dateRange}
                </span>
              ) : null}
              {visit.citiesLine ? (
                <span className="rounded-full bg-white/12 px-3 py-1.5 text-xs font-semibold backdrop-blur">
                  {visit.citiesLine}
                </span>
              ) : null}
            </div>
          )}
        </div>
      </section>
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-[#5a0000] to-[#8a0000] text-white py-12 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-[url('/placeholder.svg?height=100&width=100')] opacity-5"></div>
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="md:w-2/3 text-center md:text-left mb-8 md:mb-0">
              <h1 className="text-3xl md:text-4xl font-bold mb-2 text-[#ffd700]">
                {t("hero.title")}
              </h1>
              <h2 className="text-xl md:text-2xl font-semibold mb-4">{t("hero.subtitle")}</h2>
              <p className="text-lg opacity-90 max-w-2xl">
                {t("navLinks.schedule")} {t("common.of")} {t("hero.title")}
              </p>
            </div>
            <div className="md:w-1/4 flex justify-center">
              <div className="w-24 h-24 md:w-40 md:h-40 rounded-full overflow-hidden border-4 border-[#ffd700] shadow-xl">
                <img
                  src="/assets/Prabhushree ji 01_.webp"
                  alt={t("hero.title")}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  style={{ objectPosition: "center 20%" }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-xl overflow-hidden border border-gray-100">
          {/* Header */}
          <div className="bg-[#5a0000] text-white p-6 flex flex-col md:flex-row justify-between items-center">
            <h2 className="text-2xl font-bold mb-4 md:mb-0 text-[#ffd700]">
              {t("schedule.scheduleYear", { year: new Date().getFullYear() })}
            </h2>
            {!loading && !error && scheduleItems.length > 0 && (
              <div className="text-sm opacity-75">
                {t("schedule.eventsCount", {
                  filtered: filteredItems.length,
                  total: scheduleItems.length,
                })}
              </div>
            )}
          </div>

          {/* Show filters only when data is loaded */}
          {!loading && !error && scheduleItems.length > 0 && (
            <div className="bg-[#FBF7EE] p-6 border-b border-[#efe4cf]">
              <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto">
                  <button
                    onClick={() => setActiveMonth("All")}
                    className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                      activeMonth === "All"
                        ? "bg-[#5a0000] text-white"
                        : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    {t("schedule.allEvents", { count: scheduleItems.length })}
                  </button>
                  {months.map((month) => {
                    const monthCount = scheduleItems.filter((item) => item.month === month).length;
                    // "Upcoming" is stored as a sentinel; translate only the label shown to users
                    const monthLabel = month === "Upcoming" ? t("schedule.upcoming") : month;
                    return (
                      <button
                        key={month}
                        onClick={() => setActiveMonth(month)}
                        className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                          activeMonth === month
                            ? "bg-[#5a0000] text-white"
                            : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
                        }`}
                      >
                        {monthLabel} ({monthCount})
                      </button>
                    );
                  })}
                </div>
                <div className="relative w-full md:w-64">
                  <Search
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    size={18}
                  />
                  <input
                    type="text"
                    placeholder={t("schedule.searchPlaceholder")}
                    className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-[#5a0000]/30"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Schedule List */}
          <div className="bg-[#FBF7EE]">
            {loading ? (
              <LoadingState />
            ) : error ? (
              <ErrorState error={error} onRetry={handleRetry} />
            ) : filteredItems.length > 0 ? (
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredItems.map((item, index) => (
                  <ScheduleItem
                    key={item.id}
                    item={item}
                    index={index}
                    isLoaded={isLoaded}
                    onRequestAppointment={handleRequestAppointment}
                  />
                ))}
              </div>
            ) : (
              <EmptyState searchQuery={searchQuery} activeMonth={activeMonth} />
            )}
          </div>
        </div>
      </div>

      {/* Appointment Request Modal */}
      {appointmentModalOpen && selectedItem && (
        <AppointmentRequestModal
          isOpen={appointmentModalOpen}
          onClose={handleCloseModal}
          scheduleItem={selectedItem}
          onSubmit={handleAppointmentSubmit}
        />
      )}
    </div>
  );
}

// Supporting Components
function ErrorState({ error, onRetry }) {
  const { t } = useTranslation();
  return (
    <div className="p-12 text-center">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-1">{t("schedule.error.title")}</h3>
      <p className="text-gray-500 mb-4">{error}</p>
      <button
        onClick={onRetry}
        className="bg-[#5a0000] hover:bg-[#8a0000] text-white px-4 py-2 rounded-lg transition-colors"
      >
        {t("common.tryAgain")}
      </button>
    </div>
  );
}

function EmptyState({ searchQuery, activeMonth }) {
  const { t } = useTranslation();
  return (
    <div className="p-12 text-center">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <Calendar className="w-8 h-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-1">{t("schedule.empty.title")}</h3>
      <p className="text-gray-500">
        {searchQuery
          ? t("schedule.empty.noMatchSearch", { query: searchQuery })
          : activeMonth === "All"
            ? t("schedule.empty.noEventsPeriod")
            : t("schedule.empty.noEventsMonth", { month: activeMonth })}
      </p>
    </div>
  );
}

function ScheduleItem({ item, index, isLoaded, onRequestAppointment }) {
  const { t } = useTranslation();
  const isEvent = item.type === "event";
  const isFull = isEvent && item.availableSeats <= 0;
  const canRequest = item.request && !isFull;
  const hasEndDate =
    item.startDate !== item.endDate &&
    item.endDate !== "Not Available" &&
    item.startDate !== "Not Available";

  return (
    <div
      className={`bg-white rounded-2xl border border-[#efe4cf] shadow-sm overflow-hidden flex flex-col transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${
        item.isHighlighted ? "ring-2 ring-[#ffd700]" : ""
      }`}
      style={{
        opacity: isLoaded ? 1 : 0,
        transform: isLoaded ? "translateY(0)" : "translateY(20px)",
        transition: `opacity 500ms ${index * 60}ms, transform 500ms ${index * 60}ms`,
      }}
    >
      {/* Date band */}
      <div className="bg-[#5a0000] px-5 py-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Calendar size={18} className="text-[#ffd700] flex-shrink-0" />
          <span className="text-[#ffd700] font-bold text-lg leading-tight truncate">
            {item.startDate === "Not Available"
              ? t("schedule.modal.toBeAnnounced")
              : item.startDate}
          </span>
        </div>
        {item.city && (
          <span className="bg-[#7a0000] text-[#ffe9a8] text-xs px-3 py-1 rounded-full whitespace-nowrap flex-shrink-0">
            {item.city}
          </span>
        )}
      </div>

      {/* Body */}
      <div className="p-5 flex-1 flex flex-col">
        <h3 className="text-xl font-bold text-[#5a0000] leading-snug">{item.location}</h3>
        <div className="mt-2 space-y-1 text-sm text-gray-600">
          {hasEndDate && (
            <p className="flex items-center gap-1.5">
              <Calendar size={15} className="text-[#b8860b] flex-shrink-0" />
              {t("schedule.modal.to")} {item.endDate}
            </p>
          )}
          {item.note && (
            <p className="flex items-start gap-1.5">
              <MapPin size={15} className="text-[#b8860b] mt-0.5 flex-shrink-0" />
              <span>{item.note}</span>
            </p>
          )}
        </div>

        {(isFull || canRequest) && (
          <>
            <div className="h-px bg-[#f0e6d4] my-4" />
            <div className="mt-auto">
              {isFull ? (
                <span className="block text-center bg-gray-100 text-gray-500 py-3 rounded-lg text-sm font-medium">
                  Registration full
                </span>
              ) : (
                <>
                  <p
                    className={`text-sm mb-3 flex items-center gap-1.5 ${
                      isEvent && item.availableSeats <= 5 ? "text-amber-700" : "text-emerald-700"
                    }`}
                  >
                    <span className="w-2 h-2 rounded-full bg-current inline-block" />
                    {isEvent
                      ? item.availableSeats <= 5
                        ? "A few seats left"
                        : "Seats available"
                      : "Open for appointments"}
                  </p>
                  <button
                    onClick={() => onRequestAppointment(item)}
                    className="w-full bg-[#5a0000] hover:bg-[#8a0000] text-white py-3 rounded-lg text-sm font-medium transition-colors"
                  >
                    {isEvent ? "Register" : t("schedule.modal.title")}
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
